import React, { useMemo } from "react";
import { formatCurrency } from "../utils/format";
import { fxSettings } from "../utils/renderTier";
import "./animations.css";

// De laag die over het dashboard heen ligt. Alles hier is pointer-events:none
// en verdwijnt vanzelf: de hook haalt een run uit de lijst zodra hij klaar is.
//
// Animaties die een bestaand onderdeel aansturen (meter, podium, rijen) staan
// hier niet - die lopen via een klasse op de dashboard-div, zie animations.css.
//
// Hoeveel deeltjes er vallen hangt af van de kwaliteitstrap, zie
// utils/renderTier.js. Op een trage Pi is het aantal elementen dat tegelijk
// beweegt het verschil tussen vloeiend en haperen, dus dat is het eerste wat
// we terugschroeven. De vorm van de animatie blijft gelijk: dezelfde tijden,
// dezelfde richting, alleen dunner bezaaid.

const colors = ["#ffffff", "#ffd166", "#ff9a3c", "#7fd8ff", "#cfeaff"];

// Vaste willekeur: één keer uitrekenen per run, anders springt alles zodra
// React opnieuw tekent.
const spread = (count, build) => Array.from({ length: count }, (_, i) => build(i));

// Evenredig uitdunnen: elke zoveelste uit de lijst in plaats van de eerste
// paar. Zo blijven de knallen over het hele beeld verdeeld en blijven ze na
// elkaar komen, ook als er maar een handvol overblijft.
const thin = (list, keep) => {
  if (keep >= list.length) return list;
  const step = list.length / keep;
  return spread(keep, (i) => list[Math.floor(i * step)]);
};

// De balk bovenlangs. Het slot eromheen klipt hem af op de hoogte van de
// topbalk, zodat hij daar onder vandaan komt in plaats van er dwars over.
// De lijn onderin loopt leeg en is de klok: zodra die op is gaat hij omhoog.
const SaleBar = ({ name, amount, service }) => (
  <div className="fx-bar-slot">
    <div className="fx-bar">
      <div className="fx-bar-tag">Nieuwe verkoop</div>
      <div className="fx-bar-name">{name}</div>
      {service && <div className="fx-bar-service">{service}</div>}
      <div className="fx-bar-amount">€{formatCurrency(amount)}</div>
      <div className="fx-bar-timer" />
    </div>
  </div>
);

// De confetti draait ook mee onder de munten- en diamantregen. Daar heeft hij
// een korter venster, dus tellen en tijden zijn instelbaar: houd
// maxDelay + minDuration + durationSpread onder de `ms` van die animatie.
//
// count is een verhouding en geen vast aantal: 1 is een volle bui, en de
// regens hieronder vragen om een derde daarvan. Hoeveel snippers dat zijn
// bepaalt de kwaliteitstrap.
const ConfettiBurst = ({
  count = 1,
  maxDelay = 1.6,
  minDuration = 2.6,
  durationSpread = 2,
}) => {
  const bits = useMemo(
    () =>
      spread(Math.max(6, Math.round(fxSettings().confettiBits * count)), (i) => ({
        left: Math.random() * 100,
        delay: Math.random() * maxDelay,
        duration: minDuration + Math.random() * durationSpread,
        size: 8 + Math.random() * 10,
        round: i % 3 === 0,
        color: colors[i % colors.length],
      })),
    [count, maxDelay, minDuration, durationSpread],
  );

  return (
    <div className="fx-confetti">
      {bits.map((bit, i) => (
        <span
          key={i}
          className={`fx-confetti-bit${bit.round ? " round" : ""}`}
          style={{
            left: `${bit.left}%`,
            width: `${bit.size}px`,
            height: `${bit.size * (bit.round ? 1 : 0.45)}px`,
            background: bit.color,
            animationDelay: `${bit.delay}s`,
            animationDuration: `${bit.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

const CoinRain = () => {
  const coins = useMemo(
    () =>
      spread(fxSettings().coinCount, () => ({
        left: Math.random() * 100,
        delay: Math.random() * 1.8,
        duration: 2.2 + Math.random() * 1.4,
        size: 30 + Math.random() * 26,
        drift: -60 + Math.random() * 120,
      })),
    [],
  );

  return (
    <>
      <ConfettiBurst count={0.32} maxDelay={1.2} minDuration={2.4} durationSpread={1.6} />
      <div className="fx-coins">
        {coins.map((coin, i) => (
          <span
            key={i}
            className="fx-coin"
            style={{
              left: `${coin.left}%`,
              width: `${coin.size}px`,
              height: `${coin.size}px`,
              fontSize: `${coin.size * 0.56}px`,
              animationDelay: `${coin.delay}s`,
              animationDuration: `${coin.duration}s`,
              "--drift": `${coin.drift}px`,
            }}
          >
            €
          </span>
        ))}
      </div>
    </>
  );
};

const GemRain = () => {
  const gems = useMemo(
    () =>
      spread(fxSettings().gemCount, () => ({
        left: Math.random() * 100,
        delay: Math.random() * 1.4,
        duration: 2.8 + Math.random() * 1.6,
        size: 34 + Math.random() * 28,
        spin: 300 + Math.random() * 300,
        drift: -50 + Math.random() * 100,
      })),
    [],
  );

  return (
    <>
      <ConfettiBurst count={0.32} maxDelay={1.2} minDuration={2.6} durationSpread={1.8} />
      <div className="fx-gems">
        {gems.map((gem, i) => (
          <span
            key={i}
            className="fx-gem"
            style={{
              left: `${gem.left}%`,
              width: `${gem.size}px`,
              height: `${gem.size}px`,
              animationDelay: `${gem.delay}s`,
              animationDuration: `${gem.duration}s`,
              "--spin": `${gem.spin}deg`,
              "--drift": `${gem.drift}px`,
            }}
          />
        ))}
      </div>
    </>
  );
};

const RankUp = ({ name, position, from }) => (
  <div className="fx-rankup">
    <div className="fx-rankup-arrow">▲</div>
    <div className="fx-rankup-text">
      <div className="fx-rankup-name">{name}</div>
      <div className="fx-rankup-move">
        {from ? `van #${from} naar ` : "nu "}
        <b>#{position}</b>
      </div>
    </div>
  </div>
);

// Vuurwerk hoort bij een gebroken record. Zonder tekst is het alleen maar
// beweging, dus de kaart vertelt wie het is, voor hoeveel, en waar het oude
// record stond. Het beeld gaat er donker achter, anders valt witte tekst weg
// tegen de lichte kaarten van het dashboard.
//
// De knallen staan langs de randen: het midden blijft vrij voor de tekst.
const burstSpots = [
  { x: 14, y: 20, color: "#ffd166", delay: 0 },
  { x: 84, y: 16, color: "#7fd8ff", delay: 0.15 },
  { x: 30, y: 44, color: "#ff9a3c", delay: 0.3 },
  { x: 70, y: 38, color: "#ffffff", delay: 0.45 },
  { x: 8, y: 62, color: "#cfeaff", delay: 0.6 },
  { x: 92, y: 56, color: "#ffd166", delay: 0.75 },
  { x: 46, y: 12, color: "#7fd8ff", delay: 0.95 },
  { x: 22, y: 82, color: "#ff9a3c", delay: 1.15 },
  { x: 78, y: 84, color: "#ffffff", delay: 1.35 },
  { x: 58, y: 66, color: "#ffd166", delay: 1.55 },
  { x: 36, y: 26, color: "#cfeaff", delay: 1.8 },
  { x: 66, y: 20, color: "#ff9a3c", delay: 2 },
  { x: 12, y: 40, color: "#ffffff", delay: 2.2 },
  { x: 88, y: 34, color: "#7fd8ff", delay: 2.4 },
  { x: 42, y: 86, color: "#ffd166", delay: 2.6 },
  { x: 62, y: 46, color: "#ff9a3c", delay: 2.85 },
  { x: 26, y: 62, color: "#7fd8ff", delay: 3.05 },
  { x: 74, y: 70, color: "#cfeaff", delay: 3.25 },
];

// De vonken zijn platte bolletjes zonder gloed eromheen. Een box-shadow met
// blur per vonk betekent honderden vlakken die de browser niet kan
// doorschuiven maar per frame opnieuw moet tekenen, en daar loopt een Pi op
// stuk. De kleuren zijn fel genoeg om het zonder die gloed te doen.
const Fireworks = ({ name, amount, service, previous }) => {
  const bursts = useMemo(() => {
    const fx = fxSettings();

    // Twee ringen per knal: een dichte kern en een wijdere buitenrand. Dat
    // leest van een afstand als één bol vonken in plaats van een kring losse
    // stippen. Op de laagste trap blijft alleen de buitenring over, want die
    // bepaalt de vorm.
    const rings = fx.burstRings > 1 ? [0.55, 1] : [1];

    return thin(burstSpots, fx.burstSpots).map((burst) => {
      const count = fx.burstBits + Math.floor(Math.random() * 8);
      const bits = [];
      rings.forEach((ring, r) => {
        spread(count, (i) => {
          const angle = ((i + r * 0.5) / count) * Math.PI * 2;
          const distance = (140 + Math.random() * 150) * ring;
          bits.push({
            tx: Math.cos(angle) * distance,
            ty: Math.sin(angle) * distance,
            size: (11 + Math.random() * 9) * (rings.length > 1 && !r ? 0.7 : 1),
          });
        });
      });
      return { ...burst, bits };
    });
  }, []);

  return (
    <div className="fx-fireworks">
      <div className="fx-fireworks-dim" />

      {bursts.map((burst, i) => (
        <div
          key={i}
          className="fx-burst"
          style={{ left: `${burst.x}%`, top: `${burst.y}%` }}
        >
          {burst.bits.map((bit, j) => (
            <span
              key={j}
              className="fx-burst-bit"
              style={{
                background: burst.color,
                animationDelay: `${burst.delay}s`,
                width: `${bit.size}px`,
                height: `${bit.size}px`,
                margin: `${-bit.size / 2}px 0 0 ${-bit.size / 2}px`,
                "--tx": `${bit.tx}px`,
                "--ty": `${bit.ty}px`,
              }}
            />
          ))}
        </div>
      ))}

      {name ? (
        <div className="fx-record-wrap">
          <div className="fx-record">
            <div className="fx-record-kicker">Nieuw record</div>
            <div className="fx-record-title">GROOTSTE VERKOOP OOIT</div>
            <div className="fx-record-name">{name}</div>
            <div className="fx-record-amount">€{formatCurrency(amount)}</div>
            {service ? <div className="fx-record-service">{service}</div> : null}
            {previous ? (
              <div className="fx-record-previous">
                oude record €{formatCurrency(previous)}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const TargetHit = ({ amount }) => (
  <div className="fx-target">
    <ConfettiBurst />
    <div className="fx-target-card">
      <div className="fx-target-kicker">Team target</div>
      <div className="fx-target-title">GEHAALD</div>
      {amount ? (
        <div className="fx-target-amount">€{formatCurrency(amount)}</div>
      ) : null}
    </div>
  </div>
);

const ScreenGlow = () => <div className="fx-glow" />;

const effects = {
  "sale-bar": SaleBar,
  "confetti-burst": ConfettiBurst,
  "coin-rain": CoinRain,
  "gem-rain": GemRain,
  "rank-up": RankUp,
  "screen-glow": ScreenGlow,
  fireworks: Fireworks,
  "target-hit": TargetHit,
};

const AnimationLayer = ({ active }) => (
  <div className="fx-layer" aria-hidden="true">
    {active.map((run) => {
      const Effect = effects[run.id];
      return Effect ? <Effect key={run.runId} {...run.payload} /> : null;
    })}
  </div>
);

export default AnimationLayer;
